import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, formatDuration } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Lock, Filter, X, Check, Search } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  HoverCard, HoverCardContent, HoverCardTrigger,
} from "@/components/ui/hover-card";
import { toast } from "sonner";

const DEFAULT_HOUR_PX = 72;
const MIN_HOUR_PX = 40;
const MAX_HOUR_PX = 160;
const HOURS = Array.from({ length: 24 }, (_, h) => ({
  h,
  label: h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`,
}));

function isoDate(d) { return d.toISOString().slice(0, 10); }
function toLocalISO(d) { return new Date(d).toISOString(); }

function hexA(hex, alpha) {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
function tint(hex, ratio) {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const m = (v) => Math.round(v + (255 - v) * ratio);
  return `rgb(${m(r)}, ${m(g)}, ${m(b)})`;
}
function shade(hex, ratio) {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const m = (v) => Math.round(v * (1 - ratio));
  return `rgb(${m(r)}, ${m(g)}, ${m(b)})`;
}
function fmtTime(d) {
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// ---------- Filter chip / popover ----------
function FilterChip({ label, items, value, onChange, testIdBase, allLabel }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  useEffect(() => { if (open) setSearch(""); }, [open]);
  const selected = (value || []).map((id) => items.find((i) => i.id === id)).filter(Boolean);
  const query = search.trim().toLowerCase();
  const filtered = items.filter((i) => !query || (i.name || i.email || "").toLowerCase().includes(query));

  const toggle = (id) => {
    const next = (value || []).includes(id)
      ? (value || []).filter((x) => x !== id)
      : [...(value || []), id];
    onChange(next);
  };

  const display = selected.length === 0
    ? allLabel
    : selected.length === 1
      ? (selected[0].name || selected[0].email)
      : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`h-8 px-3 rounded-full border text-xs flex items-center gap-1.5 transition-colors ${
            selected.length > 0
              ? "border-[#145c8c] bg-[#e8f1f8] text-[#0b2c47]"
              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          }`}
          data-testid={`team-filter-${testIdBase}`}
        >
          <span className="font-medium">{label}:</span>
          <span className="truncate max-w-[140px]">{display}</span>
          {selected.length > 0 && (
            <X className="h-3 w-3 ml-1 text-slate-500"
               onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange([]); }}
               data-testid={`team-filter-${testIdBase}-clear`} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="relative mb-2">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder={`Filter ${label.toLowerCase()}…`}
                 className="w-full text-sm pl-8 pr-2 py-1.5 rounded-md border border-slate-200 focus:border-[#145c8c] outline-none"
                 data-testid={`team-filter-${testIdBase}-search`} />
        </div>
        <div className="max-h-56 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-2 py-3 text-xs text-slate-400 text-center">No matches.</div>
          ) : filtered.map((i) => {
            const isOn = (value || []).includes(i.id);
            return (
              <button key={i.id} onClick={() => toggle(i.id)}
                      className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-sm ${isOn ? "bg-slate-50" : ""}`}
                      data-testid={`team-filter-${testIdBase}-option-${i.id}`}>
                <span className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ background: i.color || "#94a3b8" }} />
                <span className="flex-1 truncate">{i.name || i.email}</span>
                {isOn && <Check className="h-3.5 w-3.5 text-[#145c8c]" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function TeamCalendarPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [day, setDay] = useState(() => { const d = new Date(); d.setHours(0,0,0,0); return d; });
  const [members, setMembers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [activities, setActivities] = useState([]);
  const [tags, setTags] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [officeClosures, setOfficeClosures] = useState([]);
  // Filters — arrays of selected IDs ([] = no filter)
  const [memberFilter, setMemberFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [activityFilter, setActivityFilter] = useState([]);
  const [tagFilter, setTagFilter] = useState([]);

  const scrollRef = useRef(null);
  const [hourPx, setHourPx] = useState(DEFAULT_HOUR_PX);

  // Current-time tick — re-render every 30s so the "now" indicator drifts down
  // the timeline when viewing today. Cheaper than the 1s tick on WeekCalendar
  // (we don't render a live-running block in the team view).
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const isDayToday = isoDate(day) === isoDate(now);
  const nowTop = isDayToday
    ? ((now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60) / 60) * hourPx
    : null;

  // Ctrl/Cmd + wheel zoom on the calendar grid. Preserves the time-of-day the
  // mouse is over while zooming so the user's focus stays put.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const anchorPx = e.clientY - rect.top + el.scrollTop;
      setHourPx((prev) => {
        const next = Math.max(MIN_HOUR_PX, Math.min(MAX_HOUR_PX, prev + (e.deltaY < 0 ? 8 : -8)));
        if (next !== prev) {
          // Adjust scrollTop so anchorPx stays under the mouse after rescale
          const minutes = (anchorPx / prev) * 60;
          requestAnimationFrame(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTop = (minutes / 60) * next - (e.clientY - rect.top);
            }
          });
        }
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [members.length]);

  useEffect(() => {
    if (user && user.role !== "admin") nav("/timer", { replace: true });
  }, [user, nav]);

  const dayEnd = useMemo(() => {
    const e = new Date(day); e.setHours(23, 59, 59, 999); return e;
  }, [day]);

  const loadMeta = useCallback(async () => {
    try {
      const [m, c, a, t, ph, oc] = await Promise.all([
        api.get("/users"),
        api.get("/clients"),
        api.get("/activities"),
        api.get("/tags"),
        api.get("/public-holidays"),
        api.get("/office-closures"),
      ]);
      setMembers(m.data);
      setClients(c.data);
      setActivities(a.data);
      setTags(t.data);
      setPublicHolidays(ph.data);
      setOfficeClosures(oc.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  const loadEntries = useCallback(async () => {
    try {
      const { data } = await api.get("/time-entries", {
        params: {
          scope: "all",
          start_date: toLocalISO(day),
          end_date: toLocalISO(dayEnd),
          include_leave: "true",
        },
      });
      setEntries(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [day, dayEnd]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  // Scroll to 6 AM once the grid has both rendered AND the browser has laid
  // out the flex container. ResizeObserver catches the moment the container
  // becomes scrollable so scrollTop is applied at the right time.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || members.length === 0) return;
    let done = false;
    const trySet = () => {
      if (done || !scrollRef.current) return;
      const c = scrollRef.current;
      if (c.scrollHeight > c.clientHeight) {
        c.scrollTop = 6 * hourPx;
        done = true;
      }
    };
    trySet();
    const raf = requestAnimationFrame(trySet);
    const ro = new ResizeObserver(trySet);
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members.length]);

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const activityMap = useMemo(() => Object.fromEntries(activities.map((a) => [a.id, a])), [activities]);
  const tagMap = useMemo(() => Object.fromEntries(tags.map((t) => [t.id, t])), [tags]);

  // Apply Client / Activity / Category filters to each entry.
  const entryMatchesFilters = useCallback((e) => {
    if (clientFilter.length > 0) {
      const ec = Array.isArray(e.client_ids) && e.client_ids.length
        ? e.client_ids
        : (e.client_id ? [e.client_id] : []);
      if (!ec.some((id) => clientFilter.includes(id))) return false;
    }
    if (activityFilter.length > 0) {
      const hasNoActivityOpt = activityFilter.includes("__none__");
      const restFilters = activityFilter.filter((id) => id !== "__none__");
      const isNoActivity = !e.activity_id;
      const matchesNamed = restFilters.length > 0 && e.activity_id && restFilters.includes(e.activity_id);
      const matchesNone = hasNoActivityOpt && isNoActivity;
      if (!matchesNamed && !matchesNone) return false;
    }
    if (tagFilter.length > 0) {
      const ets = Array.isArray(e.tag_ids) ? e.tag_ids : [];
      if (!ets.some((id) => tagFilter.includes(id))) return false;
    }
    return true;
  }, [clientFilter, activityFilter, tagFilter]);

  const filteredEntries = useMemo(
    () => entries.filter(entryMatchesFilters),
    [entries, entryMatchesFilters],
  );

  // Filter to entries that intersect the selected day, then group by user.
  const entriesByUser = useMemo(() => {
    const groups = new Map();
    for (const m of members) groups.set(m.id, []);
    for (const e of filteredEntries) {
      if (!e.user_id) continue;
      if (!groups.has(e.user_id)) groups.set(e.user_id, []);
      groups.get(e.user_id).push(e);
    }
    return groups;
  }, [filteredEntries, members]);

  const stepDay = (delta) => {
    const d = new Date(day);
    d.setDate(d.getDate() + delta);
    setDay(d);
  };

  const isToday = isoDate(day) === isoDate(new Date());
  const dateLabel = day.toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  // Members visible after applying the member filter (and only those with
  // entries matching the other filters when *any* filter is active).
  const filtersActive = clientFilter.length + activityFilter.length + tagFilter.length + memberFilter.length > 0;

  const orderedMembers = useMemo(() => {
    let pool = members;
    if (memberFilter.length > 0) pool = pool.filter((m) => memberFilter.includes(m.id));
    const withCount = pool.map((m) => ({
      ...m,
      _count: (entriesByUser.get(m.id) || []).length,
      _seconds: (entriesByUser.get(m.id) || []).reduce((acc, e) => acc + (e.duration_seconds || 0), 0),
    }));
    // When non-member filters are active, hide members that no longer have any matching entry.
    const visible = (clientFilter.length || activityFilter.length || tagFilter.length)
      ? withCount.filter((m) => m._count > 0)
      : withCount;
    return visible.sort((a, b) => {
      if (b._count !== a._count) return b._count - a._count;
      return (a.name || a.email).localeCompare(b.name || b.email);
    });
  }, [members, entriesByUser, memberFilter, clientFilter.length, activityFilter.length, tagFilter.length]);

  const totalSecondsAllMembers = useMemo(
    () => orderedMembers.reduce((acc, m) => acc + m._seconds, 0),
    [orderedMembers],
  );

  // Build a label for the active-filter summary line ("Revenue across all team members: 25h 43m").
  const filterContextLabel = useMemo(() => {
    const parts = [];
    if (tagFilter.length) {
      parts.push(tagFilter.map((id) => tagMap[id]?.name).filter(Boolean).join(", "));
    }
    if (activityFilter.length) {
      parts.push(activityFilter.map((id) => id === "__none__" ? "No activity" : activityMap[id]?.name).filter(Boolean).join(", "));
    }
    if (clientFilter.length) {
      parts.push(clientFilter.map((id) => clientMap[id]?.name).filter(Boolean).join(", "));
    }
    if (memberFilter.length) {
      const memberNames = memberFilter
        .map((id) => members.find((m) => m.id === id))
        .filter(Boolean)
        .map((m) => m.name || m.email)
        .join(", ");
      parts.push(memberNames);
    }
    return parts.join(" · ");
  }, [tagFilter, activityFilter, clientFilter, memberFilter, tagMap, activityMap, clientMap, members]);

  const clearAllFilters = () => {
    setMemberFilter([]); setClientFilter([]); setActivityFilter([]); setTagFilter([]);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="team-calendar-page">
      {/* Top bar */}
      <div className="h-16 px-6 border-b border-slate-200 flex items-center bg-white">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>
          Team Calendar
        </h1>
        <span className="ml-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-400">
          <Lock className="h-3 w-3" /> Read-only
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" className="h-9 w-9 p-0" onClick={() => stepDay(-1)}
                  data-testid="team-day-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <button
            onClick={() => { const d = new Date(); d.setHours(0,0,0,0); setDay(d); }}
            className={`h-9 px-3 text-sm rounded-md border transition-colors ${
              isToday ? "bg-[#145c8c] text-white border-[#145c8c]"
                      : "bg-white text-slate-700 border-slate-200 hover:border-[#145c8c]"
            }`}
            data-testid="team-day-today"
          >
            Today
          </button>
          <Button variant="outline" className="h-9 w-9 p-0" onClick={() => stepDay(1)}
                  data-testid="team-day-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <div className="ml-2 text-sm text-slate-600 timer-font" data-testid="team-day-label">
            {dateLabel}
          </div>
          {(() => {
            const dayIso = isoDate(day);
            const ph = publicHolidays.find((p) => p.date === dayIso);
            const closure = officeClosures.find((c) => dayIso >= c.start_date && dayIso <= c.end_date);
            if (ph) {
              return (
                <span
                  className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5"
                  title={ph.name}
                  data-testid="team-day-ph-badge"
                >
                  Public holiday · {ph.name}
                </span>
              );
            }
            if (closure) {
              return (
                <span
                  className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                  title={closure.name}
                  data-testid="team-day-closure-badge"
                >
                  Office closed · {closure.name}
                </span>
              );
            }
            return null;
          })()}
        </div>
      </div>

      {/* Filters strip */}
      <div className="px-6 py-2.5 border-b border-slate-100 bg-white flex items-center gap-2 flex-wrap"
           data-testid="team-filters-strip">
        <Filter className="h-3.5 w-3.5 text-slate-400" />
        <span className="text-xs text-slate-500 uppercase tracking-widest mr-1">Filters</span>        <FilterChip label="Members" items={members} value={memberFilter}
                    onChange={setMemberFilter} testIdBase="members" allLabel="All members" />
        <FilterChip label="Client" items={clients} value={clientFilter}
                    onChange={setClientFilter} testIdBase="clients" allLabel="All clients" />
        <FilterChip
          label="Activity"
          items={[{ id: "__none__", name: "No activity", color: "#94a3b8" }, ...activities]}
          value={activityFilter}
          onChange={setActivityFilter}
          testIdBase="activities" allLabel="All activities" />
        <FilterChip label="Category" items={tags} value={tagFilter}
                    onChange={setTagFilter} testIdBase="categories" allLabel="All categories" />
        {filtersActive && (
          <button onClick={clearAllFilters}
                  className="ml-auto text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                  data-testid="team-filters-clear-all">
            <X className="h-3 w-3" /> Clear all
          </button>
        )}
        <div className={`${filtersActive ? "" : "ml-auto"} flex items-center gap-2`}>
          <span className="text-[10px] uppercase tracking-widest text-slate-400 hidden md:inline">Zoom</span>
          <button onClick={() => setHourPx((p) => Math.max(MIN_HOUR_PX, p - 8))}
                  className="h-7 w-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
                  title="Zoom out (Ctrl/Cmd + scroll)"
                  data-testid="team-zoom-out">−</button>
          <span className="text-[11px] text-slate-500 timer-font tabular-nums w-10 text-center"
                data-testid="team-zoom-value">
            {Math.round((hourPx / DEFAULT_HOUR_PX) * 100)}%
          </span>
          <button onClick={() => setHourPx((p) => Math.min(MAX_HOUR_PX, p + 8))}
                  className="h-7 w-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
                  title="Zoom in (Ctrl/Cmd + scroll)"
                  data-testid="team-zoom-in">+</button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="px-6 py-2 border-b border-slate-100 bg-slate-50/60 flex items-center gap-6 text-xs text-slate-500">
        <span data-testid="team-member-count">
          <span className="font-semibold text-slate-700">{orderedMembers.length}</span> members
        </span>
        <span data-testid="team-total-hours">
          {filtersActive && filterContextLabel ? (
            <>
              <span className="font-medium text-slate-600">{filterContextLabel}</span> total:{" "}
            </>
          ) : (
            <>Team total: </>
          )}
          <span className="font-semibold text-slate-700 timer-font">{formatDuration(totalSecondsAllMembers)}</span>
        </span>
        <span data-testid="team-entry-count">
          <span className="font-semibold text-slate-700">{filteredEntries.length}</span>
          {filtersActive ? <> matching entries</> : <> entries</>}
        </span>
        {filtersActive && filteredEntries.length !== entries.length && (
          <span className="text-slate-400" data-testid="team-entry-count-total">
            of {entries.length} total
          </span>
        )}
      </div>

      {/* Grid */}
      {orderedMembers.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
          {filtersActive ? "No team members match the current filters." : "No members in this organisation yet."}
        </div>
      ) : (
        <div ref={scrollRef}
             className="flex-1 min-h-0 overflow-auto">
          <div className="flex min-w-max">
            {/* Hour gutter */}
            <div className="w-16 shrink-0 border-r border-slate-200 bg-white sticky left-0 z-20">
              <div className="h-12 border-b border-slate-200" />
              <div className="relative" style={{ height: hourPx * 24 }}>
                {HOURS.map((h) => (
                  <div key={h.h} className="absolute left-0 right-0 text-[10px] text-slate-400 px-2 timer-font"
                       style={{ top: h.h * hourPx - 6 }}>
                    {h.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Member columns */}
            {orderedMembers.map((m) => {
              const memberEntries = entriesByUser.get(m.id) || [];
              return (
                <MemberColumn
                  key={m.id}
                  member={m}
                  entries={memberEntries}
                  day={day}
                  clientMap={clientMap}
                  activityMap={activityMap}
                  tagMap={tagMap}
                  hourPx={hourPx}
                  nowTop={nowTop}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MemberColumn({ member, entries, day, clientMap, activityMap, tagMap, hourPx, nowTop }) {
  const dayStart = new Date(day); dayStart.setHours(0,0,0,0);
  const dayEndMs = dayStart.getTime() + 24 * 60 * 60 * 1000;
  const totalSec = entries.reduce((acc, e) => acc + (e.duration_seconds || 0), 0);
  const hasEntries = entries.length > 0;

  return (
    <div className="w-56 shrink-0 border-r border-slate-100" data-testid={`team-col-${member.id}`}>
      {/* Header */}
      <div className="h-12 border-b border-slate-200 px-3 flex items-center gap-2 bg-white sticky top-0 z-10">
        <div className="h-7 w-7 rounded-full flex items-center justify-center text-white text-[10px] font-semibold shrink-0"
             style={{ background: member.color || "#145c8c" }}>
          {(member.name || member.email || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-800 truncate">
            {member.name || member.email}
          </div>
          <div className="text-[10px] text-slate-500 timer-font" data-testid={`team-col-total-${member.id}`}>
            {formatDuration(totalSec)}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="relative bg-white" style={{ height: hourPx * 24 }}>
        {/* Hour lines */}
        {HOURS.map((h) => (
          <div key={h.h} className="absolute left-0 right-0 border-t border-slate-100"
               style={{ top: h.h * hourPx }} />
        ))}
        {!hasEntries && (
          <div className="absolute inset-0 flex items-center justify-center text-[11px] text-slate-300 pointer-events-none">
            No entries
          </div>
        )}
        {entries.map((e) => (
          <ReadOnlyEntry
            key={e.id}
            entry={e}
            dayStartMs={dayStart.getTime()}
            dayEndMs={dayEndMs}
            clientMap={clientMap}
            activityMap={activityMap}
            tagMap={tagMap}
            memberColor={member.color || "#145c8c"}
            hourPx={hourPx}
          />
        ))}
        {/* Current-time indicator — only rendered when the selected day is
            today. A single continuous line appears across all member columns
            because they share the same 24h height + hourPx scale. */}
        {nowTop != null && (
          <div className="absolute left-0 right-0 pointer-events-none z-30"
               style={{ top: nowTop - 1 }}
               data-testid={`team-current-time-line-${member.id}`}>
            <div className="absolute left-0 -translate-x-1/2 -translate-y-1/2 top-1/2 h-3 w-3 rounded-full bg-[#145c8c] shadow-md ring-2 ring-white" />
            <div className="h-0.5 bg-[#145c8c]" />
          </div>
        )}
      </div>
    </div>
  );
}

function ReadOnlyEntry({ entry, dayStartMs, dayEndMs, clientMap, activityMap, tagMap, memberColor, hourPx }) {
  const startMs = new Date(entry.start).getTime();
  const endMs = entry.end ? new Date(entry.end).getTime() : Date.now();
  // Clamp to selected day so cross-midnight entries render the portion inside the day.
  const visStart = Math.max(startMs, dayStartMs);
  const visEnd = Math.min(endMs, dayEndMs);
  if (visEnd <= visStart) return null;
  const startMin = (visStart - dayStartMs) / 60000;
  const endMin = (visEnd - dayStartMs) / 60000;
  const top = (startMin / 60) * hourPx;
  const height = Math.max(20, ((endMin - startMin) / 60) * hourPx);

  const entryClientIds = Array.isArray(entry.client_ids) && entry.client_ids.length
    ? entry.client_ids
    : (entry.client_id ? [entry.client_id] : []);
  const entryClients = entryClientIds.map((id) => clientMap[id]).filter(Boolean);
  const client = entryClients[0] || null;
  const activity = entry.activity_id ? activityMap[entry.activity_id] : null;
  const primaryCat = Array.isArray(entry.tag_ids) && entry.tag_ids.length ? tagMap?.[entry.tag_ids[0]] : null;
  const color = entry.leave_type ? "#64748b" : (primaryCat?.color || client?.color || activity?.color || memberColor);
  const isLeave = !!entry.leave_type;
  const baseBg = tint(color, 0.78);
  const stripeOverlay = isLeave
    ? `repeating-linear-gradient(135deg, ${hexA(color, 0.32)} 0 6px, transparent 6px 12px)`
    : null;

  const durationSec = Math.max(0, Math.floor((new Date(entry.end || Date.now()) - new Date(entry.start)) / 1000));
  const durLabel = `${Math.floor(durationSec / 3600)}h ${String(Math.floor((durationSec % 3600) / 60)).padStart(2, "0")}m`;
  const startLabel = fmtTime(new Date(entry.start));
  const endLabel = entry.end ? fmtTime(new Date(entry.end)) : "running";

  return (
    <HoverCard openDelay={120} closeDelay={60}>
      <HoverCardTrigger asChild>
        <div
          className="absolute left-1 right-1 rounded-md px-2 py-1 text-xs overflow-hidden border-l-4 pointer-events-auto cursor-help"
          style={{
            top,
            height,
            background: stripeOverlay ? `${stripeOverlay}, ${baseBg}` : baseBg,
            borderLeftColor: color,
            color: shade(color, 0.35),
          }}
          data-testid={`team-entry-${entry.id}`}
          data-leave={isLeave ? "true" : "false"}
        >
          <div className="font-semibold truncate">
            {isLeave ? (
              <span className="text-[9px] uppercase tracking-widest opacity-70">
                {entry.leave_type === "annual" ? "Annual Leave"
                  : entry.leave_type === "personal" ? "Personal Leave"
                  : "Sick Leave"}
              </span>
            ) : (entry.description || "(no description)")}
          </div>
          {height > 32 && (
            <div className="flex items-center gap-1.5 text-[10px] mt-0.5 opacity-80">
              {entryClients.length > 0 && (
                <span className="truncate">
                  {entryClients.length === 1 ? entryClients[0].name : `${entryClients[0].name} +${entryClients.length - 1}`}
                </span>
              )}
              {entryClients.length > 0 && activity && <span>·</span>}
              {activity && <span className="truncate">{activity.name}</span>}
            </div>
          )}
          <div className="absolute bottom-0.5 right-1.5 timer-font text-[10px] opacity-70">
            {startLabel} – {endLabel}
          </div>
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 p-3 text-sm" side="right" align="start"
                        data-testid={`team-entry-hover-${entry.id}`}>
        <div className="flex items-start gap-2 mb-2">
          <span className="h-3 w-3 rounded-full mt-1 shrink-0" style={{ background: color }} />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-800 break-words">
              {isLeave
                ? (entry.leave_type === "annual" ? "Annual Leave"
                    : entry.leave_type === "personal" ? "Personal Leave"
                    : "Sick Leave")
                : (entry.description || "(no description)")}
            </div>
            <div className="text-xs text-slate-500 timer-font tabular-nums mt-0.5">
              {startLabel} – {endLabel} <span className="text-slate-300 mx-1">·</span> {durLabel}
            </div>
          </div>
        </div>
        <div className="space-y-1.5 mt-2">
          {entryClients.length > 0 && (
            <div className="flex items-start gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5 w-16">Client</span>
              <div className="flex items-center gap-1 flex-wrap flex-1">
                {entryClients.map((c) => (
                  <span key={c.id} className="px-1.5 py-0.5 rounded-full text-[11px] font-semibold"
                        style={{ background: `${c.color}1a`, color: c.color }}>
                    {c.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {primaryCat && (
            <div className="flex items-start gap-1.5">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5 w-16">Category</span>
              <span className="px-1.5 py-0.5 rounded-full text-[11px] font-semibold"
                    style={{ background: `${primaryCat.color}1a`, color: primaryCat.color }}>
                {primaryCat.name}
              </span>
            </div>
          )}
          {activity && (
            <div className="flex items-start gap-1.5">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5 w-16">Activity</span>
              <span className="inline-flex items-center gap-1 text-slate-700">
                <span className="h-2 w-2 rounded-full" style={{ background: activity.color }} />
                {activity.name}
              </span>
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
